import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField, useTheme } from "@mui/material";
import { ButtonV1, ModalBox } from "components";
import { moneyFormat } from "utils";

const ApproveRejectModal = ({ open, onClose, data, actionType, onSubmit }) => {
  const theme = useTheme();
  const [comments, setComments] = useState("");

  useEffect(() => {
    if (open) {
      setComments("");
    }
  }, [open, data]);

  if (!data) return null;

  const isApprove = actionType === "approve";
  const isCancel = actionType === "cancel";
  const isReject = actionType === "reject";

  const title = isApprove
    ? "Approve payment instrument"
    : isCancel
    ? "Cancel payment instrument"
    : "Reject payment instrument";

  const actionButtonText = isApprove
    ? "Approve"
    : isCancel
    ? "Submit"
    : "Reject";

  const isCommentRequired = isApprove || isReject || isCancel;
  const isActionDisabled = isCommentRequired && !comments.trim();

  const handleAction = async () => {
    if (isActionDisabled) return;
    if (onSubmit) {
      await onSubmit({
        requestId: data?.requestId,
        ordNumber: data?.ordNumber,
        comments,
      });
    }
  };

  return (
    <ModalBox open={open} onCloseModal={onClose} width="580px">
      <Box p={1}>
        <Typography
          variant="h3"
          sx={{
            mb: 7,
          }}
        >
          {title}
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1.1fr 1.3fr 1.1fr",
            rowGap: 7,
            columnGap: 2.5,
            mb: 6,

            "& .MuiTypography-root:first-of-type": {
              color: theme.palette.text.secondary,
              fontSize: 14,
              mb: 0.5,
            },

            "& .MuiTypography-root:last-of-type": {
              fontWeight: 700,
              fontSize: 14,
            },
          }}
        >
          <Box>
            <Typography>Order No</Typography>
            <Typography>{data?.ordNumber || "-"}</Typography>
          </Box>

          <Box>
            <Typography>Customer name</Typography>
            <Typography>{data?.customerName || "-"}</Typography>
          </Box>

          <Box>
            <Typography>Block amount</Typography>
            <Typography>
              {data?.blockAmount != null
                ? moneyFormat(data.bypassAmount)
                : "-"}
            </Typography>
          </Box>

          <Box>
            <Typography>Requested by</Typography>
            <Typography>{data?.requestedBy || "-"}</Typography>
          </Box>

          <Box>
            <Typography>Requested at</Typography>
            <Typography>{data?.requestedAt || "-"}</Typography>
          </Box>

          <Box>
            <Typography>Available limit</Typography>
            <Typography>
              {data?.availableLimit != null
                ? moneyFormat(data.availableLimit)
                : "-"}
            </Typography>
          </Box>

          <Box>
            <Typography>Current instrument</Typography>
            <Typography>{data?.fromPaymentMode || "-"}</Typography>
          </Box>

          <Box sx={{ gridColumn: "span 2" }}>
            <Typography>Requested instrument</Typography>
            <Typography>{data?.toPaymentMode || "-"}</Typography>
          </Box>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={2.5}
          placeholder={
            isCommentRequired ? "Enter comments (mandatory)" : "Enter comments"
          }
          value={comments}
          onChange={(e) => setComments(e.target.value)}
        />

        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
          <ButtonV1 variant="outlined" title="Cancel" onClick={onClose} />
          <ButtonV1
            variant="contained"
            title={actionButtonText}
            onClick={handleAction}
            disabled={isActionDisabled}
          />
        </Box>
      </Box>
    </ModalBox>
  );
};

ApproveRejectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  actionType: PropTypes.oneOf(["approve", "reject", "cancel"]),
  onSubmit: PropTypes.func,
};

export default ApproveRejectModal;
